import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Settings as SettingsIcon, Save, Shield, Mail, Bell,
    Database, Clock, Globe, Palette, Key, AlertCircle,
    CheckCircle, Info, Loader2, RefreshCw, Download,
    Upload, FileText, Users, Eye, EyeOff
} from 'lucide-react';
import { apiMethods } from '@/services/api';

const Settings = () => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('general');
    const [showApiKey, setShowApiKey] = useState(false);
    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState({});

    // Fetch settings
    const { data: settings, isLoading } = useQuery({
        queryKey: ['adminSettings'],
        queryFn: async () => {
            const response = await apiMethods.admin.getSettings();
            return response.data;
        }
    });

    const [formData, setFormData] = useState({
        general: {
            siteName: '',
            siteUrl: '',
            adminEmail: '',
            timezone: '',
            dateFormat: '',
            maintenanceMode: false
        },
        email: {
            provider: 'gmail',
            fromName: '',
            fromEmail: '',
            replyToEmail: '',
            gmailUser: '',
            gmailClientId: '',
            gmailClientSecret: '',
            gmailRefreshToken: ''
        },
        security: {
            passwordMinLength: 8,
            passwordRequireUppercase: true,
            passwordRequireLowercase: true,
            passwordRequireNumbers: true,
            passwordRequireSpecial: true,
            sessionTimeout: 24,
            maxLoginAttempts: 5,
            enforce2FA: false
        },
        features: {
            allowRegistration: false,
            requireEmailVerification: true,
            enableAnalytics: true,
            enablePublicProfiles: true,
            enablevCardDownload: true,
            enableQRCode: true,
            maxProfileImageSize: 5,
            allowedImageFormats: ['jpg', 'jpeg', 'png', 'gif']
        },
        api: {
            enableAPI: false,
            apiKey: '',
            rateLimitPerHour: 100
        }
    });

    // Update settings mutation
    const updateSettingsMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.admin.updateSettings(data);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['adminSettings']);
            setSuccess({ [variables.section]: 'Settings saved successfully!' });
            setTimeout(() => setSuccess({}), 3000);
        },
        onError: (error, variables) => {
            setErrors({
                [variables.section]: error.response?.data?.message || 'Failed to save settings'
            });
        }
    });

    // Test email mutation
    const testEmailMutation = useMutation({
        mutationFn: async (email) => {
            const response = await apiMethods.admin.testEmail({ email });
            return response.data;
        },
        onSuccess: () => {
            setSuccess({ testEmail: 'Test email sent successfully!' });
            setTimeout(() => setSuccess({}), 3000);
        },
        onError: (error) => {
            setErrors({
                testEmail: error.response?.data?.message || 'Failed to send test email'
            });
        }
    });

    // Clear analytics mutation
    const clearAnalyticsMutation = useMutation({
        mutationFn: async () => {
            const response = await apiMethods.admin.clearAnalytics();
            return response.data;
        },
        onSuccess: () => {
            setSuccess({ clearAnalytics: 'Analytics data cleared successfully!' });
            setTimeout(() => setSuccess({}), 3000);
        },
        onError: (error) => {
            setErrors({
                clearAnalytics: error.response?.data?.message || 'Failed to clear analytics'
            });
        }
    });

    // Backup database mutation
    const backupDatabaseMutation = useMutation({
        mutationFn: async () => {
            const response = await apiMethods.admin.backupDatabase();
            return response.data;
        },
        onSuccess: (data) => {
            const url = window.URL.createObjectURL(new Blob([JSON.stringify(data)]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `backup-${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
    });

    // Initialize form data when settings load
    useEffect(() => {
        if (settings) {
            setFormData({
                general: settings.general || formData.general,
                email: settings.email || formData.email,
                security: settings.security || formData.security,
                features: settings.features || formData.features,
                api: settings.api || formData.api
            });
        }
    }, [settings]);

    const handleInputChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));

        // Clear errors
        if (errors[section]) {
            setErrors(prev => ({ ...prev, [section]: '' }));
        }
    };

    const handleSaveSection = (section) => {
        updateSettingsMutation.mutate({
            section,
            data: formData[section]
        });
    };

    const generateApiKey = () => {
        const key = 'dbcapi_' + Array.from({ length: 32 }, () =>
            'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 62)]
        ).join('');

        handleInputChange('api', 'apiKey', key);
    };

    const tabs = [
        { id: 'general', label: 'General', icon: SettingsIcon },
        { id: 'email', label: 'Email', icon: Mail },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'features', label: 'Features', icon: Globe },
        { id: 'api', label: 'API', icon: Key },
        { id: 'maintenance', label: 'Maintenance', icon: Database }
    ];

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Admin Settings</h1>
                    <p className="mt-2 text-gray-600">
                        Configure system settings and preferences
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Tabs Navigation */}
                    <div className="lg:w-64">
                        <nav className="space-y-1">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-primary-100 text-primary-700'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                    >
                                        <Icon className="h-5 w-5 mr-3" />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1">
                        {/* General Settings */}
                        {activeTab === 'general' && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h2 className="text-lg font-medium text-gray-900 mb-6">General Settings</h2>

                                {errors.general && (
                                    <div className="mb-4 rounded-md bg-red-50 p-4">
                                        <p className="text-sm text-red-800">{errors.general}</p>
                                    </div>
                                )}

                                {success.general && (
                                    <div className="mb-4 rounded-md bg-green-50 p-4">
                                        <p className="text-sm text-green-800">{success.general}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Site Name</label>
                                        <input
                                            type="text"
                                            value={formData.general.siteName}
                                            onChange={(e) => handleInputChange('general', 'siteName', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Site URL</label>
                                        <input
                                            type="url"
                                            value={formData.general.siteUrl}
                                            onChange={(e) => handleInputChange('general', 'siteUrl', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Admin Email</label>
                                        <input
                                            type="email"
                                            value={formData.general.adminEmail}
                                            onChange={(e) => handleInputChange('general', 'adminEmail', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Timezone</label>
                                        <select
                                            value={formData.general.timezone}
                                            onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        >
                                            <option value="UTC">UTC</option>
                                            <option value="America/New_York">Eastern Time</option>
                                            <option value="America/Chicago">Central Time</option>
                                            <option value="America/Denver">Mountain Time</option>
                                            <option value="America/Los_Angeles">Pacific Time</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={formData.general.maintenanceMode}
                                                onChange={(e) => handleInputChange('general', 'maintenanceMode', e.target.checked)}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">
                                                Enable Maintenance Mode
                                            </span>
                                        </label>
                                        <p className="mt-1 text-xs text-gray-500">
                                            When enabled, only admins can access the system
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => handleSaveSection('general')}
                                        disabled={updateSettingsMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Email Settings */}
                        {activeTab === 'email' && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h2 className="text-lg font-medium text-gray-900 mb-6">Email Settings</h2>

                                {errors.email && (
                                    <div className="mb-4 rounded-md bg-red-50 p-4">
                                        <p className="text-sm text-red-800">{errors.email}</p>
                                    </div>
                                )}

                                {success.email && (
                                    <div className="mb-4 rounded-md bg-green-50 p-4">
                                        <p className="text-sm text-green-800">{success.email}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">From Name</label>
                                        <input
                                            type="text"
                                            value={formData.email.fromName}
                                            onChange={(e) => handleInputChange('email', 'fromName', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">From Email</label>
                                        <input
                                            type="email"
                                            value={formData.email.fromEmail}
                                            onChange={(e) => handleInputChange('email', 'fromEmail', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Reply-To Email</label>
                                        <input
                                            type="email"
                                            value={formData.email.replyToEmail}
                                            onChange={(e) => handleInputChange('email', 'replyToEmail', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div className="border-t pt-6">
                                        <h3 className="text-sm font-medium text-gray-900 mb-4">Gmail OAuth2 Settings</h3>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Gmail User</label>
                                                <input
                                                    type="email"
                                                    value={formData.email.gmailUser}
                                                    onChange={(e) => handleInputChange('email', 'gmailUser', e.target.value)}
                                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Client ID</label>
                                                <input
                                                    type="text"
                                                    value={formData.email.gmailClientId}
                                                    onChange={(e) => handleInputChange('email', 'gmailClientId', e.target.value)}
                                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Client Secret</label>
                                                <input
                                                    type="password"
                                                    value={formData.email.gmailClientSecret}
                                                    onChange={(e) => handleInputChange('email', 'gmailClientSecret', e.target.value)}
                                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t pt-6">
                                        <h3 className="text-sm font-medium text-gray-900 mb-4">Test Email Configuration</h3>

                                        {errors.testEmail && (
                                            <div className="mb-4 rounded-md bg-red-50 p-4">
                                                <p className="text-sm text-red-800">{errors.testEmail}</p>
                                            </div>
                                        )}

                                        {success.testEmail && (
                                            <div className="mb-4 rounded-md bg-green-50 p-4">
                                                <p className="text-sm text-green-800">{success.testEmail}</p>
                                            </div>
                                        )}

                                        <div className="flex space-x-3">
                                            <input
                                                type="email"
                                                value={testEmailAddress}
                                                onChange={(e) => setTestEmailAddress(e.target.value)}
                                                placeholder="test@example.com"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            />
                                            <button
                                                onClick={() => testEmailMutation.mutate(testEmailAddress)}
                                                disabled={!testEmailAddress || testEmailMutation.isPending}
                                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <Mail className="h-4 w-4 mr-2" />
                                                Send Test
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => handleSaveSection('email')}
                                        disabled={updateSettingsMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Security Settings */}
                        {activeTab === 'security' && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h2 className="text-lg font-medium text-gray-900 mb-6">Security Settings</h2>

                                {errors.security && (
                                    <div className="mb-4 rounded-md bg-red-50 p-4">
                                        <p className="text-sm text-red-800">{errors.security}</p>
                                    </div>
                                )}

                                {success.security && (
                                    <div className="mb-4 rounded-md bg-green-50 p-4">
                                        <p className="text-sm text-green-800">{success.security}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-4">Password Requirements</h3>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Minimum Length
                                                </label>
                                                <input
                                                    type="number"
                                                    min="6"
                                                    max="32"
                                                    value={formData.security.passwordMinLength}
                                                    onChange={(e) => handleInputChange('security', 'passwordMinLength', parseInt(e.target.value))}
                                                    className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.security.passwordRequireUppercase}
                                                    onChange={(e) => handleInputChange('security', 'passwordRequireUppercase', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Require uppercase letters
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.security.passwordRequireLowercase}
                                                    onChange={(e) => handleInputChange('security', 'passwordRequireLowercase', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Require lowercase letters
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.security.passwordRequireNumbers}
                                                    onChange={(e) => handleInputChange('security', 'passwordRequireNumbers', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Require numbers
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.security.passwordRequireSpecial}
                                                    onChange={(e) => handleInputChange('security', 'passwordRequireSpecial', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Require special characters
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Session Timeout (hours)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="168"
                                            value={formData.security.sessionTimeout}
                                            onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                                            className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Max Login Attempts
                                        </label>
                                        <input
                                            type="number"
                                            min="3"
                                            max="10"
                                            value={formData.security.maxLoginAttempts}
                                            onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                                            className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>

                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.security.enforce2FA}
                                            onChange={(e) => handleInputChange('security', 'enforce2FA', e.target.checked)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            Enforce 2FA for all users
                                        </span>
                                    </label>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => handleSaveSection('security')}
                                        disabled={updateSettingsMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Features Settings */}
                        {activeTab === 'features' && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h2 className="text-lg font-medium text-gray-900 mb-6">Feature Settings</h2>

                                {errors.features && (
                                    <div className="mb-4 rounded-md bg-red-50 p-4">
                                        <p className="text-sm text-red-800">{errors.features}</p>
                                    </div>
                                )}

                                {success.features && (
                                    <div className="mb-4 rounded-md bg-green-50 p-4">
                                        <p className="text-sm text-green-800">{success.features}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-4">System Features</h3>

                                        <div className="space-y-3">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.allowRegistration}
                                                    onChange={(e) => handleInputChange('features', 'allowRegistration', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Allow public registration
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.requireEmailVerification}
                                                    onChange={(e) => handleInputChange('features', 'requireEmailVerification', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Require email verification
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.enableAnalytics}
                                                    onChange={(e) => handleInputChange('features', 'enableAnalytics', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Enable analytics tracking
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-4">Business Card Features</h3>

                                        <div className="space-y-3">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.enablePublicProfiles}
                                                    onChange={(e) => handleInputChange('features', 'enablePublicProfiles', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Enable public business cards
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.enablevCardDownload}
                                                    onChange={(e) => handleInputChange('features', 'enablevCardDownload', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Enable vCard downloads
                                                </span>
                                            </label>

                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.features.enableQRCode}
                                                    onChange={(e) => handleInputChange('features', 'enableQRCode', e.target.checked)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Enable QR code generation
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Max Profile Image Size (MB)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={formData.features.maxProfileImageSize}
                                            onChange={(e) => handleInputChange('features', 'maxProfileImageSize', parseInt(e.target.value))}
                                            className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => handleSaveSection('features')}
                                        disabled={updateSettingsMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* API Settings */}
                        {activeTab === 'api' && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h2 className="text-lg font-medium text-gray-900 mb-6">API Settings</h2>

                                {errors.api && (
                                    <div className="mb-4 rounded-md bg-red-50 p-4">
                                        <p className="text-sm text-red-800">{errors.api}</p>
                                    </div>
                                )}

                                {success.api && (
                                    <div className="mb-4 rounded-md bg-green-50 p-4">
                                        <p className="text-sm text-green-800">{success.api}</p>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.api.enableAPI}
                                            onChange={(e) => handleInputChange('api', 'enableAPI', e.target.checked)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            Enable API Access
                                        </span>
                                    </label>

                                    {formData.api.enableAPI && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    API Key
                                                </label>
                                                <div className="flex space-x-2">
                                                    <div className="flex-1 relative">
                                                        <input
                                                            type={showApiKey ? 'text' : 'password'}
                                                            value={formData.api.apiKey}
                                                            readOnly
                                                            className="block w-full pr-10 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowApiKey(!showApiKey)}
                                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                        >
                                                            {showApiKey ? (
                                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                                            ) : (
                                                                <Eye className="h-5 w-5 text-gray-400" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    <button
                                                        onClick={generateApiKey}
                                                        className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                    >
                                                        Generate New
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Rate Limit (requests per hour)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="10"
                                                    max="10000"
                                                    value={formData.api.rateLimitPerHour}
                                                    onChange={(e) => handleInputChange('api', 'rateLimitPerHour', parseInt(e.target.value))}
                                                    className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => handleSaveSection('api')}
                                        disabled={updateSettingsMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Maintenance */}
                        {activeTab === 'maintenance' && (
                            <div className="space-y-6">
                                {/* Database Backup */}
                                <div className="bg-white shadow rounded-lg p-6">
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">Database Backup</h2>

                                    <p className="text-sm text-gray-600 mb-4">
                                        Download a complete backup of your database including all users, profiles, and settings.
                                    </p>

                                    <button
                                        onClick={() => backupDatabaseMutation.mutate()}
                                        disabled={backupDatabaseMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        {backupDatabaseMutation.isPending ? 'Preparing...' : 'Download Backup'}
                                    </button>
                                </div>

                                {/* System Information */}
                                <div className="bg-white shadow rounded-lg p-6">
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">System Information</h2>

                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Version</span>
                                            <span className="text-sm text-gray-900 font-mono">1.0.0</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Total Users</span>
                                            <span className="text-sm text-gray-900">{settings?.stats?.totalUsers || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Total Business Cards</span>
                                            <span className="text-sm text-gray-900">{settings?.stats?.totalCards || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Storage Used</span>
                                            <span className="text-sm text-gray-900">{settings?.stats?.storageUsed || '0'} GB</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Last Backup</span>
                                            <span className="text-sm text-gray-900">
                                                {settings?.stats?.lastBackup
                                                    ? new Date(settings.stats.lastBackup).toLocaleString()
                                                    : 'Never'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Danger Zone */}
                                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                    <h2 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h2>

                                    {errors.clearAnalytics && (
                                        <div className="mb-4 rounded-md bg-red-100 p-4">
                                            <p className="text-sm text-red-800">{errors.clearAnalytics}</p>
                                        </div>
                                    )}

                                    {success.clearAnalytics && (
                                        <div className="mb-4 rounded-md bg-green-100 p-4">
                                            <p className="text-sm text-green-800">{success.clearAnalytics}</p>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <h3 className="text-sm font-medium text-red-800">Clear All Analytics Data</h3>
                                            <p className="text-sm text-red-700 mt-1">
                                                This will permanently delete all analytics data. This action cannot be undone.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Are you sure you want to clear all analytics data? This action cannot be undone.')) {
                                                        clearAnalyticsMutation.mutate();
                                                    }
                                                }}
                                                disabled={clearAnalyticsMutation.isPending}
                                                className="mt-2 inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                            >
                                                {clearAnalyticsMutation.isPending ? 'Clearing...' : 'Clear Analytics'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;